import { useCallback, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
    AD_CAMPAIGN_DECISIONS,
    CANCEL_WARMUP,
    CONFIRM_WARMUP_EXPANSION,
    DECLINE_WARMUP_EXPANSION,
    RECHECK_AD_SET_CHANGE,
    REMOVE_WARMUP_EXPANSION_AD_SET,
    RETRY_WARMUP_EXPANSION,
    SET_WARMUP_MODE,
} from '@/graphql/warmup-queries'
import { UPDATE_AD_SET_BUDGET } from '@/graphql/ads/ad-campaigns-queries'
import { showNotification } from '@/utils/notifications'
import { maximumToMinor } from '@/utils/pixel-warmup'
import { describeBudgetGuardError, describeCancelResult, refusalText } from '@/utils/pixel-warmup-panel'

const notify = (message, type = 'success', duration = 5000) =>
    showNotification(message, type, duration, null, null, true, true)

/**
 * The actions of the warm-up panel of one ad campaign. The warm-up itself is read by the page (`useWarmupEntries`) and
 * given here with `refresh` (read the page's warm-ups again) and `applyWarmup` (take over a warm-up an action answered
 * with). Every action returns whether it worked, tells the owner the answer (or the reason it was refused) and keeps
 * the panel current: an action that answers with the fresh warm-up applies it, the others read again.
 * @param {string} adCampaignId
 * @param {{ warmup: any, refresh: () => Promise<void>, applyWarmup: (adCampaignId: string, warmup: any) => void, formatAmount?: (minor: number) => string }} options
 */
export const useWarmupPanel = (adCampaignId, { warmup, refresh, applyWarmup, formatAmount }) => {
    const refetch = refresh
    const [busy, setBusy] = useState(false)
    const [confirmMutation] = useMutation(CONFIRM_WARMUP_EXPANSION)
    const [declineMutation] = useMutation(DECLINE_WARMUP_EXPANSION)
    const [retryMutation] = useMutation(RETRY_WARMUP_EXPANSION)
    const [modeMutation] = useMutation(SET_WARMUP_MODE)
    const [cancelMutation] = useMutation(CANCEL_WARMUP)
    const [removeMutation] = useMutation(REMOVE_WARMUP_EXPANSION_AD_SET)
    const [recheckMutation] = useMutation(RECHECK_AD_SET_CHANGE)
    const [budgetMutation] = useMutation(UPDATE_AD_SET_BUDGET)
    const [loadDecisions, { data: decisionsData, loading: decisionsLoading }] = useLazyQuery(AD_CAMPAIGN_DECISIONS, {
        fetchPolicy: 'network-only',
    })

    const guarded = useCallback(
        async (work, failure) => {
            setBusy(true)
            try {
                return await work()
            } catch (caught) {
                notify(describeBudgetGuardError(caught, formatAmount) || caught?.message || failure, 'error', 10000)
                return false
            } finally {
                setBusy(false)
            }
        },
        [formatAmount],
    )

    const expectedVersion = warmup?.version

    // confirm, decline, retry and mode changes answer with the fresh warm-up, which updates the cache
    const ownerAction = useCallback(
        (mutate, field, success) => (variables) =>
            guarded(async () => {
                const { data: answer } = await mutate({ variables: { adCampaignId, expectedVersion, ...variables } })
                const result = answer?.[field]
                if (result?.ok) {
                    if (result.warmup) applyWarmup(adCampaignId, result.warmup)
                    if (success) notify(success)
                    return true
                }
                notify(refusalText(result), 'error', 8000)
                if (result?.reason === 'version_changed' || result?.reason === 'busy') await refetch()
                return false
            }, 'It did not work. Try again.'),
        [adCampaignId, applyWarmup, expectedVersion, guarded, refetch],
    )

    const confirm = ownerAction(confirmMutation, 'confirmWarmupExpansion', 'The expansion has started.')
    const decline = ownerAction(declineMutation, 'declineWarmupExpansion', 'The warm-up keeps running.')
    const retry = ownerAction(retryMutation, 'retryWarmupExpansion', 'The expansion has started again.')
    const setMode = ownerAction(modeMutation, 'setWarmupMode', 'The mode was changed.')

    /** Cancels the warm-up. Returns `{ done, choiceRequired }`. */
    const cancel = useCallback(
        async (choice) => {
            let outcome = { done: false, choiceRequired: false }
            await guarded(async () => {
                const { data: answer } = await cancelMutation({
                    variables: { adCampaignId, expectedVersion, choice: choice || null },
                })
                const result = answer?.cancelWarmup
                if (result?.cancelled) {
                    notify(describeCancelResult(result), 'success', 12000)
                    outcome = { done: true, choiceRequired: false }
                } else if (result?.reason === 'choice_required') {
                    outcome = { done: false, choiceRequired: true }
                } else {
                    notify(refusalText(result), 'error', 8000)
                }
                await refetch()
            }, 'The warm-up could not be cancelled. Try again.')
            return outcome
        },
        [adCampaignId, cancelMutation, expectedVersion, guarded, refetch],
    )

    const removeHeldRow = useCallback(
        (adSetId) =>
            guarded(async () => {
                const { data: answer } = await removeMutation({ variables: { adCampaignId, adSetId } })
                const result = answer?.removeWarmupExpansionAdSet
                if (result?.removed) notify('The audience ad set was removed.')
                else notify(refusalText(result), 'error', 8000)
                await refetch()
                return !!result?.removed
            }, 'The audience ad set could not be removed. Try again.'),
        [adCampaignId, guarded, refetch, removeMutation],
    )

    const recheck = useCallback(
        (adSetId) =>
            guarded(async () => {
                const { data: answer } = await recheckMutation({ variables: { adSetId } })
                const result = answer?.recheckAdSetChange
                notify(result?.message || 'Checked with Meta.', 'success', 8000)
                await refetch()
                return true
            }, 'Meta could not be checked right now. Try again.'),
        [guarded, recheckMutation, refetch],
    )

    /** Changes the budget of a held ad set; `budget` is in major units, `expectedBudget` the stored minor-unit budget. */
    const changeHeldBudget = useCallback(
        (adSetId, budget, expectedBudget) =>
            guarded(async () => {
                const minor = maximumToMinor(budget)
                if (minor === null) {
                    notify(
                        'Enter a daily budget as a number greater than zero, for example 50 or 50.25.',
                        'error',
                        5000,
                    )
                    return false
                }
                await budgetMutation({
                    variables: {
                        adSetId,
                        budget: minor,
                        ...(Number.isFinite(expectedBudget) ? { expectedBudget } : {}),
                    },
                })
                notify('The budget was saved.')
                await refetch()
                return true
            }, 'The budget could not be saved. Try again.'),
        [budgetMutation, guarded, refetch],
    )

    const showHistory = useCallback(
        () => loadDecisions({ variables: { adCampaignId, limit: 50 } }),
        [adCampaignId, loadDecisions],
    )

    return {
        busy,
        confirm,
        decline,
        retry,
        setMode,
        cancel,
        removeHeldRow,
        recheck,
        changeHeldBudget,
        showHistory,
        decisions: decisionsData?.AdCampaign?.adCampaignDecisions || null,
        decisionsLoading,
        expectedVersion,
        refetch,
    }
}
